import { ConfigStateService } from '@abp/ng.core';
import {
  Component,
  OnInit,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { NgbDateStruct, NgbDateParserFormatter, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerI18n, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LdmDatePipe } from 'src/app/core/pipes/ldm-date.pipe';
import { CustomDatepickerI18n } from 'src/app/core/localizations/datetime-picker/datepicker-i18n';
import { DateDisplayType } from 'src/app/core/enums/date-display-type.enum';

@Component({
  selector: 'datetime-picker',
  templateUrl: './datetime-picker.component.html',
  styleUrls: ['./datetime-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatetimePickerComponent),
    },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
  ],
})
export class DatetimePickerComponent implements OnInit, ControlValueAccessor {
  _disable: boolean = false;
  _allowNull: boolean = false;
  _doNotResetOnUnit: boolean = false;
  isRTL: boolean;

  @ViewChild('dtp', { static: true, read: NgbDatepicker })
  dtp: NgbDatepicker;

  date: Date;
  @Input()
  minDate: Date;
  @Input()
  maxDate: Date;
  @Input()
  showTime: boolean = true;
  @Input()
  doNotDisplayClearButton: boolean = false;
  @Input()
  showCalendarIconButton: boolean = false;
  
  @Output()
  changed: EventEmitter<{ id: number }> = new EventEmitter();
  @Output()
  timeChanged: EventEmitter<{ time: NgbTimeStruct }> = new EventEmitter();

  get formatedMinDate(): NgbDateStruct {
    if (this.minDate) return this.getNgbStructDate(this.minDate);
    else return null;
  }
  get formatedMaxDate(): NgbDateStruct {
    if (this.maxDate) {
      return this.getNgbStructDate(this.maxDate);
    } else {
      return null;
    }
  }

  @Input()
  set disabled(value: boolean) {
    this._disable = value;
  }

  get disabled(): boolean {
    return this._disable;
  }

  @Input()
  set allowNull(value: boolean) {
    this._allowNull = value;
  }

  get allowNull(): boolean {
    return this._allowNull;
  }

  @Input()
  set doNotResetOnInit(value: boolean) {
    this._doNotResetOnUnit = value;
  }

  get doNotResetOnInit(): boolean {
    return this._doNotResetOnUnit;
  }

  ngbDate: NgbDateStruct;
  ngbTime: NgbTimeStruct;
  ngbDateModel: string;
  dateTime: string;
  constructor(
    private parserFormatter: NgbDateParserFormatter,
    private ldmDatePie: LdmDatePipe,
    private config: ConfigStateService
  ) {}

  ngOnInit(): void {
    const localize = this.config.getOne('localization');
    this.isRTL = localize?.currentCulture.cultureName === 'ar';
  }

  ngAfterViewInit() {
    if (this.allowNull === true && this.doNotResetOnInit === false) {
      this.resetValue();
    }
  }

  writeValue(date: Date, setNull: boolean = false): void {
    if (!date) {
      if (this._allowNull) {
        this.date = null;
        this.ngbDate = null;
        this.ngbTime = null;
        this.dateTime = '';
        this.ngbDateModel = null;

        this.onChange(this.date);
        return;
      }

      this.date = new Date();
    } else {
      if (typeof date === 'string') {
        date = new Date(date);
      }
      this.date = date;
    }

    if (typeof this.minDate === 'string') {
      this.minDate = new Date(this.minDate);
    }
    if (typeof this.maxDate === 'string') {
      this.maxDate = new Date(this.maxDate);
    }

    if (this.date) {
      if (this.maxDate && this.date > this.maxDate) {
        this.date.setTime(this.maxDate.getTime());
      }

      if (this.minDate && this.date < this.minDate) {
        this.date.setTime(this.minDate.getTime());
      }

      this.ngbDate = this.getNgbStructDate(this.date);
      this.ngbTime = this.getNgbTimeStruct(this.date);

      this.ngbDateModel = this.ldmDatePie.transform(this.date, DateDisplayType.Date);
      this.setDateTime();
      this.applyChanges();
    }
  }
  resetValue() {
    this.date = null;
    this.ngbDate = null;
    this.ngbTime = null;
    this.dateTime = '';
    this.ngbDateModel = null;
    this.onChange(this.date);
    this.changed.emit();
    this.timeChanged.emit({ time: this.ngbTime });
  }
  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }
  onChange: Function = (_: Date) => {};
  onTouched: Function = (_: Date) => { };
  onDateSelect(date: NgbDateStruct) {
    this.ngbDate = date;
    this.syncAndValidate();
  }

  onTimeChange(eventArgs) {
    this.syncAndValidate();
  }

  checkDate() {
    let date = this.combineDateTime(this.ngbDate, this.ngbTime);
    if (this.minDate && date < this.minDate && this.ngbTime) {
      this.ngbTime = this.getNgbTimeStruct(this.minDate);
    } else if (this.maxDate && date > this.maxDate && this.ngbTime) {
      this.ngbTime = this.getNgbTimeStruct(this.maxDate);
    }
  }

  getNgbStructDate(date: Date): NgbDateStruct {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const ngbDateStruct = {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
    return ngbDateStruct;
  }

  getNgbTimeStruct(date: Date): NgbTimeStruct {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const ngbTimeStruct = {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
    return ngbTimeStruct;
  }


  combineDateTime(date: NgbDateStruct, time: NgbTimeStruct): Date {
    if (date && time) {
      const combinedDate = new Date(
        date.year,
        date.month - 1,
        date.day,
        time.hour,
        time.minute,
        time.second
      );
      return combinedDate;
    } else if (date) {
      const combinedDate = new Date(date.year, date.month - 1, date.day);
      return combinedDate;
    }
  }

  setDateTime() {
    var displayType = this.showTime ? DateDisplayType.DateTimeWithoutSeconds : DateDisplayType.Date;
    if (!this.ngbTime) {
      displayType = DateDisplayType.Date;
    }

    if (this.ngbDate) {
      this.dateTime = this.ldmDatePie.transform(
        this.combineDateTime(this.ngbDate, this.ngbTime),
        displayType
      );
      this.navigateToDate(this.date);
    }
  }
  applyChanges(): void {
    this.date = this.combineDateTime(this.ngbDate, this.ngbTime);
    this.onChange(this.date);
    this.changed.emit();
  }
  applyChangesOnTime(): void {
    this.timeChanged.emit({ time : this.ngbTime });
  }
  getTimeStr(time: number): string {
    return time < 10 ? '0' + time.toString() : time.toString();
  }

  navigateToDate(date: Date): void {
    //TODO:Workaround clear model bug;
    this.dtp.navigateTo({ year: 1900, month: 1 });
    this.dtp.navigateTo(this.getNgbStructDate(date));
  }

  private syncAndValidate() {
    this.checkDate();
    this.applyChanges();
    this.setDateTime();
    this.applyChangesOnTime();
  }
}
